/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Box, useTheme, Theme, Skeleton } from '@mui/material'

export interface ArticleCardSkeletonProps {
  className?: string
  'data-testid'?: string
}

export function ArticleCardSkeleton({
  className,
  'data-testid': testId,
}: ArticleCardSkeletonProps) {
  const theme = useTheme()

  return (
    <Box css={skeletonContainer(theme)} className={className} data-testid={testId}>
      {/* Thumbnail section */}
      <Box className="thumbnail">
        <Skeleton
          variant="rectangular"
          width="100%"
          height={176}
          sx={{ backgroundColor: theme.palette.grey[200] }}
        />
        {/* Format icon skeleton */}
        <Box className="formatIcon">
          <Skeleton
            variant="circular"
            width={52}
            height={52}
            sx={{ backgroundColor: theme.palette.grey[300] }}
          />
        </Box>
      </Box>

      {/* Label section */}
      <Box className="label">
        {/* Format text skeleton */}
        <Skeleton
          variant="text"
          width={80}
          height={14}
          sx={{
            fontSize: '12px',
            marginBottom: '4px',
          }}
        />

        {/* Title skeleton - 2 lines */}
        <Box className="title">
          <Skeleton variant="text" width="100%" height={22} sx={{ fontSize: '18px' }} />
          <Skeleton variant="text" width="70%" height={22} sx={{ fontSize: '18px' }} />
        </Box>

        {/* Description skeleton */}
        <Skeleton
          variant="text"
          width="60%"
          height={16}
          sx={{
            fontSize: '14px',
            marginTop: '8px',
          }}
        />

        {/* Button skeleton */}
        <Box className="button">
          <Skeleton variant="text" width={60} height={20} sx={{ fontSize: '14px' }} />
        </Box>
      </Box>
    </Box>
  )
}

export default ArticleCardSkeleton

const skeletonContainer = (theme: Theme) => css`
  background-color: ${theme.palette.background.paper || '#ffffff'};
  border-radius: 8px 8px 60px 0px;
  overflow: hidden;
  width: 264px;
  height: 348px;
  filter: drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.15));
  display: flex;
  flex-direction: column;
  position: relative;

  .thumbnail {
    height: 176px;
    min-height: 176px;
    width: 100%;
    position: relative;

    .formatIcon {
      position: absolute;
      bottom: -20px;
      left: 24px;
      width: 52px;
      height: 52px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      z-index: 1;
    }
  }

  .label {
    padding: 36px;
    flex: 1;
    position: relative;

    .title {
      margin-top: 4px;
      height: 44px;
      max-height: 44px;
      width: 100%;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      gap: 0px;
    }

    .button {
      position: absolute;
      bottom: 32px;
      right: 36px;
    }
  }
`
